import { isSameTrip, pickAvailableTrip } from "./tourTripDedup";

export const FREE_DAY_SELECTION_ID = "__free_day__";

export const isFreeDaySelectionId = (id) => id === FREE_DAY_SELECTION_ID;

export const isFreeDayTrip = (trip) =>
  !trip?.description && (!trip?.attractions || trip.attractions.length === 0);

export const createFreeDayFromSlot = (slot) => ({
  ...slot,
  id: undefined,
  description: "",
  attractions: [],
});

export const getTemplatePoolForSlot = (template, slot, city) => {
  if (!slot) return [];

  const { startingPoint, destination } = slot;
  const routePool = template.filter(
    (entry) =>
      entry.startingPoint === startingPoint && entry.destination === destination
  );

  if (!isFreeDayTrip(slot) && routePool.length > 0) {
    return routePool;
  }

  const cityPool = template.filter(
    (entry) => entry.startingPoint === city && entry.destination === city
  );

  if (cityPool.length > 0) {
    return cityPool;
  }

  return routePool;
};

export const getGlobalDayNumber = (tour, stayIndex, tripIndex) => {
  if (!tour) return tripIndex + 1;

  let offset = 0;
  for (let i = 0; i < stayIndex; i++) {
    offset += tour[i].trips.length;
  }
  return offset + tripIndex + 1;
};

export const getUsedTripsWithLocation = (tour, city, editingContext = {}) => {
  if (!tour || !city) return [];

  return tour
    .flatMap((group, stayIndex) =>
      group.destination !== city
        ? []
        : group.trips.map((trip, tripIndex) => ({
            trip,
            stayIndex,
            tripIndex,
            date: trip.date,
            dayNumber: getGlobalDayNumber(tour, stayIndex, tripIndex),
          }))
    )
    .filter(
      ({ stayIndex, tripIndex }) =>
        !(
          stayIndex === editingContext.stayIndex &&
          tripIndex === editingContext.tripIndex
        )
    );
};

export const getTripOptionsForDay = (tour, template, { stayIndex, tripIndex }) => {
  const slot = tour?.[stayIndex]?.trips?.[tripIndex];
  if (!slot) {
    return { current: null, available: [], used: [], route: null, isFreeDay: false };
  }

  const city = tour[stayIndex]?.destination;
  const freeDay = isFreeDayTrip(slot);
  const pool = getTemplatePoolForSlot(template, slot, city);
  const { startingPoint, destination } = slot;

  const usedLocations = getUsedTripsWithLocation(tour, city, {
    stayIndex,
    tripIndex,
  });

  const available = [];
  const used = [];
  const usedFreeDays = [];

  pool.forEach((templateTrip) => {
    if (!freeDay && isSameTrip(slot, templateTrip)) return;

    const usedOn = usedLocations.find((loc) => isSameTrip(loc.trip, templateTrip));
    if (usedOn) {
      used.push({ trip: templateTrip, usedOn });
    } else {
      available.push({ trip: templateTrip });
    }
  });

  usedLocations.forEach((location) => {
    if (isFreeDayTrip(location.trip)) {
      usedFreeDays.push({ usedOn: location });
    }
  });

  return {
    current: slot,
    available,
    used,
    usedFreeDays,
    showFreeDayOption: !freeDay,
    route: { startingPoint, destination },
    isFreeDay: freeDay,
  };
};

export const tripFromTemplate = (templateTrip, slot) => ({
  ...slot,
  id: templateTrip.id,
  description: templateTrip.description || "",
  attractions: templateTrip.attractions || [],
  startingPoint: templateTrip.startingPoint,
  destination: templateTrip.destination,
});

export const applyFreeDayToSlot = (tour, stayIndex, tripIndex) => {
  const slot = tour[stayIndex].trips[tripIndex];
  const freeDay = createFreeDayFromSlot(slot);

  return tour.map((group, groupIndex) => {
    if (groupIndex !== stayIndex) return group;
    return {
      ...group,
      trips: group.trips.map((trip, index) =>
        index === tripIndex ? freeDay : trip
      ),
    };
  });
};

export const applyTripToSlot = (tour, stayIndex, tripIndex, templateTrip) => {
  const slot = tour[stayIndex].trips[tripIndex];
  const updatedTrip = tripFromTemplate(templateTrip, slot);

  return tour.map((group, groupIndex) => {
    if (groupIndex !== stayIndex) return group;
    return {
      ...group,
      trips: group.trips.map((trip, index) =>
        index === tripIndex ? updatedTrip : trip
      ),
    };
  });
};

export const swapTripsBetweenSlots = (
  tour,
  targetStayIndex,
  targetTripIndex,
  sourceStayIndex,
  sourceTripIndex
) => {
  const targetTrip = { ...tour[targetStayIndex].trips[targetTripIndex] };
  const sourceTrip = { ...tour[sourceStayIndex].trips[sourceTripIndex] };

  return tour.map((group, groupIndex) => ({
    ...group,
    trips: group.trips.map((trip, tripIndex) => {
      if (groupIndex === targetStayIndex && tripIndex === targetTripIndex) {
        return { ...sourceTrip, date: targetTrip.date };
      }
      if (groupIndex === sourceStayIndex && tripIndex === sourceTripIndex) {
        return { ...targetTrip, date: sourceTrip.date };
      }
      return trip;
    }),
  }));
};

export const buildPreservedTripsByDate = (tour) => {
  if (!tour) return {};

  const preserved = {};
  tour.forEach((group) => {
    group.trips.forEach((trip) => {
      if (!trip?.date) return;
      preserved[trip.date] = {
        id: trip.id,
        description: trip.description,
        attractions: trip.attractions,
        startingPoint: trip.startingPoint,
        destination: trip.destination,
      };
    });
  });
  return preserved;
};

export const countItineraryProgress = (tour) => {
  if (!tour?.length) return { withActivity: 0, total: 0 };

  const trips = tour.flatMap((group) => group.trips);
  const total = trips.length;
  const withActivity = trips.filter((trip) => !isFreeDayTrip(trip)).length;

  return { withActivity, total };
};

export const resolveTripForDate = (
  template,
  { startingPoint, destination, date },
  usedTripsForDestination,
  preservedByDate
) => {
  const preserved = preservedByDate?.[date];
  const suitableTrips = template.filter(
    (entry) =>
      entry.startingPoint === startingPoint && entry.destination === destination
  );

  if (
    preserved &&
    isFreeDayTrip(preserved) &&
    preserved.startingPoint === startingPoint &&
    preserved.destination === destination
  ) {
    return null;
  }

  if (
    preserved &&
    preserved.startingPoint === startingPoint &&
    preserved.destination === destination
  ) {
    const preservedTemplate = suitableTrips.find((entry) =>
      preserved.id ? entry.id === preserved.id : isSameTrip(entry, preserved)
    );
    if (
      preservedTemplate &&
      !usedTripsForDestination.some((used) => isSameTrip(used, preservedTemplate))
    ) {
      return preservedTemplate;
    }
  }

  return pickAvailableTrip(suitableTrips, usedTripsForDestination);
};
